//************************************************************************ /
// RecipientList
// Reusable component for the recipients in thank you note and the invite note flows
//************************************************************************ /

import React, { memo, useCallback } from "react";
import { Avatar, List } from "@dreambigger/design-system/src/components";
import { generateCloudinaryUrl } from "@dreambigger/shared/src/utils";
import { getIcon } from "@dreambigger/design-system/src/utils";
import { useMixpanel, useGA, useSegment } from "@dreambigger/shared/src/hooks";
import InspireAnalytics, {
  InspireAnalyticEvents,
  InspireAnalyticsHelper,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";

// type key will most likely be used for thank you notes only.
export type Recipient = {
  email: string;
  id?: number;
  firstName?: string;
  lastName?: string;
  imageId?: string;
  imageVersion?: string;
  meta?: null;
  type?: "rsvp" | "shared_story" | "donated" | "0";
};

type Props = {
  users: Recipient[];
  onDelete: Function;
  usersLoading?: boolean;
  title?: string;
  emptyText?: string;
  style?: React.CSSProperties;
  className?: string;
};

// Check if the recipient has an image to display on the avatar.
const hasImages = (recipient: Recipient) =>
  recipient.imageId && recipient.imageVersion && recipient.imageId !== "avatar";

// Get the first character from the recipient's first and last name to display on avatar.
const getUserInitials = (recipient: Recipient) =>
  `${recipient?.firstName?.charAt(0)}${recipient?.lastName?.charAt(0)}`;

/**
 * This component can be used to display a list of recipients
 * It also has the ability to remove a user from the list via the onDelete
 */
const RecipientCard = ({
  users,
  onDelete,
  className = "",
  usersLoading = false,
  title = "Recipient List",
  emptyText = "Your recipients will appear here. Who would you like to thank?",
  style = {
    boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
    backgroundColor: "white",
    overflow: "auto",
    maxHeight: "300px",
    minHeight: "300px",
  },
}: Props) => {
  // Handle the removal of a user from the list.
  const handleDelete = useCallback(
    (removedUser: Recipient) => {
      logAnalytics();
      onDelete(removedUser);
    },
    [users]
  );

  // Load Analytics and Appcues
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  const logAnalytics = () => {
    // Log analytics when user clicks a button.
    googleAnalytics.event({
      category: "Button Click",
      action: `Recipient deleted from list`,
      label: window.location.href,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.INVITE_CLICK,
      properties: {
        Page: window.location.href,
        "Button Clicked": "Delete Recipient From List",
      },
      mixpanel,
      segment,
    });
  };

  return (
    <List
      // Class can be inherited as a prop. Add scrollbar if list exceeds 3 invitees.
      className={className + ` ${users.length > 3 && "overflow-y-scroll"}`}
      loading={usersLoading}
      // The title that will appear as the header in the list.
      header={
        <div className="tc">
          <span className="fwb">{title}</span> (send to {users.length}{" "}
          {users.length !== 1 ? "recipients" : "recipient"}):
        </div>
      }
      style={style}
      locale={{
        // The text that will display if there are no users available to display.
        emptyText: <p>{emptyText}</p>,
      }}
      dataSource={users}
      renderItem={(user) => (
        <List.Item key={user.email} className="pa-4 mh-2">
          <List.Item.Meta
            avatar={
              <Avatar
                size="large"
                alt="user avatar"
                src={
                  hasImages(user) &&
                  generateCloudinaryUrl(user.imageVersion!, user.imageId!, {
                    type: "avatar",
                  })
                }
              >
                {!hasImages(user) && (
                  <div className="usn">{getUserInitials(user)}</div>
                )}
              </Avatar>
            }
            title={
              <span className="fwb">
                {`${user.firstName} ${user.lastName}`}
              </span>
            }
            description={
              <>
                <span>{user?.email}</span>
                <br></br>
                <span className="fsi">{user?.type}</span>
              </>
            }
          />
          <div
            className="pointer"
            style={{ color: "grey" }}
            onClick={() => handleDelete(user)}
          >
            {getIcon("trash")}
          </div>
        </List.Item>
      )}
    ></List>
  );
};

export default memo(RecipientCard);
